import { buildHeaders } from "src/utils/apiUtils";
import authService from './authService';


let BASE_URL = process.env.REACT_APP_API_URL

export async function loadConfig() {
  // .env is used directly; no encrypted config to load.
}

loadConfig().then(() => {
});


export async function getComplianceDashboard(_tenantId?: string) {

    try {
      const headers = await buildHeaders(BASE_URL);
      const response = await fetch(`${BASE_URL}/compliance-control-actions/dashboard`, {
        method: 'GET',
        headers,
        credentials: "include",
      });


      const data = await response.json();
    if (!response.ok ) {
        if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
            throw new Error("Forbidden: You do not have permission to access this resource.");
        }
        throw new Error("Failed to fetch");
    }
    return data;
} catch (error: any) {
    throw new Error(error);
}

}


export async function getComplianceForEachStandardDashboard(_tenantId?: string) {
  try {
    const headers = await buildHeaders(BASE_URL);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/compliance-counts`, {
      method: 'GET',
      headers,
      credentials: "include",
    });


    const data = await response.json();
    if (!response.ok ) {
        if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
            throw new Error("Forbidden: You do not have permission to access this resource.");
        }
        throw new Error("Failed to fetch");
    }
    return data;
} catch (error: any) {
    throw new Error(error);
}

}


// Unassigned work (no supervisor / no manager) — supervisor, leader and admin dashboards.
export async function getUnassignedDashboard() {
  try {
    const headers = await buildHeaders(BASE_URL);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/dashboard/unassigned`, {
      method: 'GET',
      headers,
      credentials: "include",
    });

    const data = await response.json();
    if (!response.ok ) {
        if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
            throw new Error("Forbidden: You do not have permission to access this resource.");
        }
        throw new Error("Failed to fetch");
    }
    return data;
} catch (error: any) {
    throw new Error(error);
}

}


export async function getUsersTasksDashboard() {
  try {
    const headers = await buildHeaders(BASE_URL);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/in-progress-tasks`, {
      method: 'GET',
      headers,
    credentials: "include",
  });


  const data = await response.json();
    if (!response.ok ) {
        if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
            throw new Error("Forbidden: You do not have permission to access this resource.");
        }
        throw new Error("Failed to fetch");
    }
    return data;
} catch (error: any) {
    throw new Error(error);
}
}



export async function fetchRecentAssignments() {
  try {
    const headers = await buildHeaders(BASE_URL);
    const response = await fetch(`${BASE_URL}/publicSurveyUser/recent-assignments`, {
      method: 'GET',
      headers,
    credentials: "include",
  });


  const data = await response.json();
    if (!response.ok ) {
        if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
            throw new Error("Forbidden: You do not have permission to access this resource.");
        }
        throw new Error("Failed to fetch");
    }
    return data;
} catch (error: any) {
    throw new Error(error);
}
}

/**
 * Surveys linked (via PublicSurveyFunction) to any of the given function ids —
 * the "surveys relevant to my function" dashboard widget. A survey can be
 * linked to multiple functions and a user can belong to multiple functions;
 * this returns the union (any matching function), deduped server-side.
 */
export async function fetchSurveysForFunctions(functionIds: string[], perPage = 10) {
  if (!functionIds.length) return { success: true, data: [] };
  try {
    const headers = await buildHeaders(BASE_URL);
    const params = new URLSearchParams({
      query: '',
      perPage: String(perPage),
      pageNo: '1',
      functionIds: functionIds.join(','),
    });
    const response = await fetch(`${BASE_URL}/publicSurvey?${params.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      if (response.status === 401 || response.status === 403 || data.message === 'Forbidden access') {
        throw new Error('Forbidden: You do not have permission to access this resource.');
      }
      throw new Error('Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

export async function fetchComplianceActions() {
  try {
    const headers = await buildHeaders(BASE_URL);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/recent`, {
      method: 'GET',
      headers,
    credentials: "include",
  });


  const data = await response.json();
    if (!response.ok ) {
        if (response.status === 401 || response.status === 403 || data.message === "Forbidden access") {
            throw new Error("Forbidden: You do not have permission to access this resource.");
        }
        throw new Error("Failed to fetch");
    }
    return data;
} catch (error: any) {
    throw new Error(error);
}
}

/**
 * One page of a compliance dashboard bucket, for the drill-down modals.
 *
 * getComplianceDashboard() returns every row of every bucket in one payload;
 * the drill-downs page against this instead so a modal costs one page, not the
 * whole task table. Bucket keys match the backend BUCKET_WHERE map.
 */
export async function fetchDashboardBucketPage(
  { bucket, perPage, pageNo, search, standardId, dueFrom, dueTo, sortBy, sortOrder }: {
    bucket: string; perPage: number; pageNo: number; search?: string;
    /** The dashboard's standard filter — keeps the drill matching the card. */
    standardId?: string;
    /** The dashboard's due-date filter, `YYYY-MM-DD`. */
    dueFrom?: string; dueTo?: string;
    sortBy?: string; sortOrder?: 'asc' | 'desc';
  },
) {
  try {
    const headers = await buildHeaders(BASE_URL);
    const params = new URLSearchParams({ bucket, perPage: String(perPage), pageNo: String(pageNo) });
    if (search) params.set('search', search);
    if (standardId) params.set('standardId', standardId);
    if (dueFrom) params.set('dueFrom', dueFrom);
    if (dueTo) params.set('dueTo', dueTo);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/dashboard-bucket?${params.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

/** One page of a single function's in-progress tasks, for the Workload drill-down. */
export async function fetchWorkloadTasksPage(
  { functionId, perPage, pageNo, search, sortBy, sortOrder }: {
    functionId: string; perPage: number; pageNo: number; search?: string;
    sortBy?: string; sortOrder?: 'asc' | 'desc';
  },
) {
  try {
    const headers = await buildHeaders(BASE_URL);
    const params = new URLSearchParams({ functionId, perPage: String(perPage), pageNo: String(pageNo) });
    if (search) params.set('search', search);
    if (sortBy) params.set('sortBy', sortBy);
    if (sortOrder) params.set('sortOrder', sortOrder);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/workload-tasks?${params.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

/** Server-paginated "Team Compliance Tasks — View All" drill-down feed. */
export async function fetchComplianceActionsOverview({ perPage, pageNo, search }: { perPage: number; pageNo: number; search?: string }) {
  try {
    const headers = await buildHeaders(BASE_URL);
    const params = new URLSearchParams({ perPage: String(perPage), pageNo: String(pageNo) });
    if (search) params.set('search', search);
    const response = await fetch(`${BASE_URL}/compliance-control-actions/overview?${params.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

/** Server-paginated "Survey Assignments — View All" drill-down feed. */
export async function fetchSurveyAssignmentsOverview({ perPage, pageNo, search }: { perPage: number; pageNo: number; search?: string }) {
  try {
    const headers = await buildHeaders(BASE_URL);
    const params = new URLSearchParams({ perPage: String(perPage), pageNo: String(pageNo) });
    if (search) params.set('search', search);
    const response = await fetch(`${BASE_URL}/publicSurveyUser/assignments-overview?${params.toString()}`, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.message || 'Failed to fetch');
    }
    return data;
  } catch (error: any) {
    throw new Error(error);
  }
}

export async function getAssessmentDashboard() {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/answers/assessment-dashboard`,
      { method: 'GET' }
    );
    if (!response.ok) {
      throw new Error('Failed to get assessment dashboard');
    }
    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error fetching assessment dashboard:', error);
    throw new Error(error?.message || 'Error fetching assessment dashboard');
  }
}

export async function getDetailedAssessment() {
  try {
    const response = await authService.apiCall(
      `${BASE_URL}/answers/detailed-assessment`,
      { method: 'GET' }
    );
    if (!response.ok) {
      throw new Error('Failed to get detailed assessment');
    }
    const result = await response.json();
    return result;
  } catch (error: any) {
    console.error('Error fetching detailed assessment:', error);
    throw new Error(error?.message || 'Error fetching detailed assessment');
  }
}
